import AIcon from '@/components/A/AIcon/AIcon'
import ASelect from '@/components/A/ASelect/ASelect'
import FullscreenBtn from '../FullscreenBtn/FullscreenBtn'
import styles from './AnalysisPPTView.module.less'
import cls from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimationPlaceholder } from '../AnalysisFlowView/AnalysisBlock/AnalysisBlock'
import { useTranslation } from 'react-i18next'
import { commonUtils } from '@/helpers/commonUtils'
import { message } from 'antd'
import { api } from '@/api'
import NiceModal from '@ebay/nice-modal-react'
import DownloadPptRechargeModal from '@/components/Modals/DownloadPptRechargeModal/DownloadPptRechargeModal'
import { PAGE_LINKS } from '@/router/pageConst'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'
import { PPT_TRANSLATE_LANGUAGE_OPTIONS } from '@/helpers/outputLanguage'

interface Props {
  project: ProjectModels.ProjectDetailV3
  isFullscreen: boolean
  onClick: (isFullscreen: boolean) => void
  onSwitch: () => void
}
function AnalysisPPTView(props: Props) {
  const { t } = useTranslation()
  const navigate = useNewNavigate()
  const FLOW_ANALYSIS_PREFIX = 'dashboard.experience.projectDetailPage'
  const { isFullscreen, onClick, onSwitch, project } = props
  const sourceLanguage = project.useLanguage || 'CN'
  const [selectedLanguage, setSelectedLanguage] = useState<ProjectModels.UseLanguage>(sourceLanguage)
  const [currentPptOutput, setCurrentPptOutput] = useState<string[]>(project.pptOutput)
  const [currentMergedPptPath, setCurrentMergedPptPath] = useState(project.mergedPptPath)
  const [translateStatus, setTranslateStatus] = useState<ProjectModels.PptTranslateStatus>('completed')
  const [isDownloading, setIsDownloading] = useState(false)
  const pollingTimerRef = useRef<number>()
  const pollingInFlightRef = useRef(false)
  const shouldHideFreeTrialPpt = Boolean(project.shouldHideFreeTrialPpt)
  const translateCompletedToastShownRef = useRef<Partial<Record<ProjectModels.UseLanguage, boolean>>>({})

  const stopTranslatePolling = useCallback(() => {
    if (pollingTimerRef.current) {
      window.clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = undefined
    }
    pollingInFlightRef.current = false
  }, [])

  const applyTranslateResult = useCallback((result: ProjectModels.PptTranslateResult) => {
    setTranslateStatus(result.translateStatus)
    setCurrentPptOutput(Array.isArray(result.pptOutput) ? result.pptOutput : [])
    setCurrentMergedPptPath(result.mergedPptPath || '')
  }, [])

  const pollTranslateResult = useCallback(async (language: ProjectModels.UseLanguage) => {
    if (pollingInFlightRef.current) {
      return
    }
    pollingInFlightRef.current = true
    try {
      const { data } = await api.project.pollPptTranslate(project.id, language).send()
      applyTranslateResult(data)
      if (data.translateStatus === 'completed' || data.translateStatus === 'failed') {
        if (
          data.translateStatus === 'completed'
          && language !== sourceLanguage
          && !translateCompletedToastShownRef.current[language]
        ) {
          message.success(t('dashboard.ppt.translateCompleted'))
          translateCompletedToastShownRef.current[language] = true
        }
        stopTranslatePolling()
        if (data.translateStatus === 'failed') {
          message.error(data.errorMsg || t('dashboard.ppt.exportFailed'))
        }
      }
    } catch (error) {
      const apiError = error as ApiError
      stopTranslatePolling()
      setTranslateStatus('failed')
      message.error(apiError.statusMessage || t('dashboard.ppt.exportFailed'))
    } finally {
      pollingInFlightRef.current = false
    }
  }, [applyTranslateResult, project.id, sourceLanguage, stopTranslatePolling, t])

  const startTranslatePolling = useCallback((language: ProjectModels.UseLanguage) => {
    stopTranslatePolling()
    pollingTimerRef.current = window.setInterval(() => {
      pollTranslateResult(language)
    }, 3000)
  }, [pollTranslateResult, stopTranslatePolling])

  const handleLanguageChange = useCallback(async (language: ProjectModels.UseLanguage) => {
    if (shouldHideFreeTrialPpt) {
      NiceModal.show(DownloadPptRechargeModal)
      return
    }
    setSelectedLanguage(language)
    if (language === sourceLanguage) {
      stopTranslatePolling()
      setTranslateStatus('completed')
      setCurrentPptOutput(project.pptOutput)
      setCurrentMergedPptPath(project.mergedPptPath)
      return
    }
    translateCompletedToastShownRef.current[language] = false
    setTranslateStatus('ongoing')
    try {
      const { data } = await api.project.startPptTranslate(project.id, language).send()
      applyTranslateResult(data)
      if (data.translateStatus === 'ongoing') {
        await pollTranslateResult(language)
        startTranslatePolling(language)
      } else {
        stopTranslatePolling()
      }
    } catch (error) {
      const apiError = error as ApiError
      setTranslateStatus('failed')
      message.error(apiError.statusMessage || t('dashboard.ppt.exportFailed'))
    }
  }, [
    applyTranslateResult,
    pollTranslateResult,
    project.id,
    project.mergedPptPath,
    project.pptOutput,
    shouldHideFreeTrialPpt,
    sourceLanguage,
    startTranslatePolling,
    stopTranslatePolling,
    t,
  ])

  const handlePPTDownload = useCallback(async () => {
    if (shouldHideFreeTrialPpt) {
      NiceModal.show(DownloadPptRechargeModal)
      return
    }
    if (project.status !== 'completed' || isDownloading || !currentMergedPptPath) {
      return
    }
    setIsDownloading(true)
    try {
      message.loading(t('dashboard.ppt.pptProcessingDesc3'), 0)
      await commonUtils.downloadFile(currentMergedPptPath, project.projectName + '.pptx')
      message.destroy()
      message.success(t('dashboard.ppt.exportSuccess'))
    } catch (error) {
      console.error(error)
      message.error(t('dashboard.ppt.exportFailed'))
    } finally {
      setIsDownloading(false)
    }
  }, [currentMergedPptPath, isDownloading, project, selectedLanguage, shouldHideFreeTrialPpt, t])

  useEffect(() => {
    if (selectedLanguage === sourceLanguage) {
      setCurrentPptOutput(project.pptOutput)
      setCurrentMergedPptPath(project.mergedPptPath)
      setTranslateStatus('completed')
    }
  }, [project.mergedPptPath, project.pptOutput, selectedLanguage, sourceLanguage])

  useEffect(() => {
    setSelectedLanguage(sourceLanguage)
    setCurrentPptOutput(project.pptOutput)
    setCurrentMergedPptPath(project.mergedPptPath)
    setTranslateStatus('completed')
    translateCompletedToastShownRef.current = {}
    stopTranslatePolling()
  }, [project.id])

  useEffect(() => {
    return () => {
      stopTranslatePolling()
    }
  }, [stopTranslatePolling])

  const isTranslating = selectedLanguage !== sourceLanguage && translateStatus === 'ongoing'
  const canDownload = project.status === 'completed' && !!currentMergedPptPath && !isDownloading && !shouldHideFreeTrialPpt
  const freeTrialPreviewSrc = commonUtils.getImagePath('projectDetailPage/free-trial-ppt-preview.png')

  const handleSubscribeClick = useCallback(() => {
    navigate(PAGE_LINKS.pricing)
  }, [navigate])

  return <div className={styles.analysisPptView}>
    <div className={styles.title}>
      <div className={styles.text}>
        <span>
          {t(`${FLOW_ANALYSIS_PREFIX}.title.text2`)}
        </span>
        {project.status === 'completed' && !shouldHideFreeTrialPpt && (
          <ASelect
            className={styles.languageSelect}
            testId='ppt-translate-language-select'
            value={selectedLanguage}
            options={PPT_TRANSLATE_LANGUAGE_OPTIONS.map((option) => ({
              label: t(option.labelKey),
              value: option.value,
            }))}
            onChange={handleLanguageChange}
          />
        )}
      </div>
      <div className={styles.btnBox}>
        {
          project.status === 'completed' && (
            <span
              className={cls(styles.pptDownloadBtn, {
                [styles.downloading]: !canDownload,
              })}
              data-testid='analysis-ppt-download-btn'
              onClick={handlePPTDownload}
              title={t('dashboard.ppt.downloadBtnText')}
            >
              {isDownloading && (
                <span className={styles.spin}>
                  <AIcon type='icon-line-global_loading' width={16} style={{ color: '#8463F9' }} />
                </span>
              )}
              {!isDownloading && (
                <AIcon type="icon-line-global_download" width={16} height={16} />
              )}
            </span>
          )
        }
        {
          isFullscreen && (
            <span className={styles.switchBtn} onClick={onSwitch} data-testid='analysis-ppt-switch-btn'>
              <AIcon type="icon-line-global_swicth" width={16} height={16}></AIcon>
            </span>
          )
        }
        <FullscreenBtn isFullscreen={isFullscreen} onClick={onClick} />
      </div>
    </div>
    <div className={cls(styles.content, shouldHideFreeTrialPpt && styles.freeTrialContent)}>
      {
        shouldHideFreeTrialPpt && (
          <div className={styles.freeTrialPptPreview}>
            <img className={styles.freeTrialPreviewImg} src={freeTrialPreviewSrc} alt="" />
            <div className={styles.freeTrialTip}>
              <p>{t(`${FLOW_ANALYSIS_PREFIX}.pptAnalysis.freeTrialLockText1`)}</p>
              <p>{t(`${FLOW_ANALYSIS_PREFIX}.pptAnalysis.freeTrialLockText2`)}</p>
              <button
                type='button'
                className={styles.subscribeBtn}
                data-testid='analysis-ppt-subscribe-btn'
                onClick={handleSubscribeClick}
              >
                {t('modals.downloadPptRechargeModal.btn')}
              </button>
            </div>
          </div>
        )
      }
      {
        currentPptOutput.length === 0 && !shouldHideFreeTrialPpt && <div className={styles.emptyBox}>
          <img src={commonUtils.getImagePath('projectDetailPage/loading.gif')} alt="" />
          <p className={styles.emptyBoxTitle}>{t(`${FLOW_ANALYSIS_PREFIX}.pptAnalysis.emptyState.waiting`)}</p>
          <p className={styles.emptyBoxDesc}>{t(`${FLOW_ANALYSIS_PREFIX}.pptAnalysis.emptyState.description`)}</p>
        </div>
      }
      {
        currentPptOutput.length > 0 && !shouldHideFreeTrialPpt && (
          <div className={styles.pptCardContainer}>
            <div className={styles.pptCardList}>
              {
                currentPptOutput.map((src,) => {
                  return <PPTCard key={src} src={src} maxWidth={isFullscreen ? '80%' : undefined} />
                })
              }
              {
                !shouldHideFreeTrialPpt && (project.progressRatio !== 100 || isTranslating) && <div>
                  <PPTCard key={'card'} src="" maxWidth={isFullscreen ? '80%' : undefined} />
                  <p className={styles.generatingText}>{t(`${FLOW_ANALYSIS_PREFIX}.pptAnalysis.generating`)}</p>
                </div>
              }
            </div>
          </div>
        )
      }
    </div>
  </div>
}

const PPTCard = (props: { src: string, maxWidth?: number | string }) => {
  const { src, maxWidth } = props
  const [imageLoaded, setImageLoaded] = useState(false)
  const [delay, setDelay] = useState(1000 * 3600 * 365)

  const handleImageLoad = () => {
    setImageLoaded(true)
    setDelay(0)
  }

  useEffect(() => {
    if (src) {
      setImageLoaded(false)
    } else {
      // 如果src为空，则展示默认的 placeholder
      setImageLoaded(true)
      setDelay(0)
    }
  }, [src])

  return <AnimationPlaceholder delay={delay}>
    {
      src
        ? <div className={cls(styles.card, imageLoaded && styles.loaded)} style={{ maxWidth }}>
          <img className={styles.pic} src={src} alt="" onLoad={handleImageLoad} />
        </div>
        : <div className={cls(styles.card, styles.loaded)} style={{ maxWidth }}>
          <div className={styles.pic}></div>
        </div>
    }
  </AnimationPlaceholder>
}

export default AnalysisPPTView
